import React, { useCallback, useEffect, useState } from "react";
import { api, fmtMoney, fmtNum, fmtPct } from "@/lib/api";
import { Link2, RotateCw, PlayCircle, TrendingUp, TrendingDown, KeyRound, Shield, Copy, ExternalLink, Trash2 } from "lucide-react";
import { toast } from "sonner";

// ---------------------------------------------------------------------------
// Style helpers — replace nested ternaries with named lookups so the JSX
// below stays readable and the mapping is testable in isolation.
// ---------------------------------------------------------------------------
const CARD_ACCENT = {
  pos: "border-l-emerald-500",
  neg: "border-l-red-500",
  warn: "border-l-amber-500",
};
const cardAccentClass = (accent) => CARD_ACCENT[accent] || "border-l-slate-700";

const trendIconFor = (trend) => {
  if (trend === "UP") return <TrendingUp size={14} className="text-pos" />;
  if (trend === "DOWN") return <TrendingDown size={14} className="text-neg" />;
  return null;
};

const sourceBadgeClass = (source, isLive) => {
  if (isLive) return "bg-emerald-500/20 text-emerald-300";
  if (source === "DEMO") return "bg-amber-500/20 text-amber-300";
  return "bg-slate-800 text-slate-500";
};

const breakoutTextClass = (state) => {
  if (state === "LONG") return "text-pos";
  if (state === "SHORT") return "text-neg";
  return "text-slate-500";
};

function ManualExchange({ onDone }) {
  const [open, setOpen] = useState(false);
  const [token, setToken] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!token.trim()) return;
    setBusy(true);
    try {
      await api.post("/zerodha/manual-exchange", { request_token: token.trim() });
      toast.success("Zerodha connected via manual exchange");
      setToken("");
      setOpen(false);
      onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Manual exchange failed — token may have expired");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="mt-2" data-testid="manual-exchange">
      <button
        onClick={() => setOpen(!open)}
        className="w-full text-[10px] uppercase tracking-[0.15em] text-slate-400 hover:text-blue-400 py-1"
        data-testid="manual-exchange-toggle"
      >
        <KeyRound size={10} className="inline mr-1" /> Paste request_token instead
      </button>
      {open && (
        <div className="mt-2 space-y-2">
          <input
            data-testid="manual-exchange-input"
            value={token}
            onChange={(e) => setToken(e.target.value)}
            placeholder="request_token from redirect URL"
            className="w-full bg-slate-900 border border-slate-700 rounded-sm px-2 py-1.5 text-xs font-mono focus:border-blue-500 outline-none"
          />
          <button
            data-testid="manual-exchange-submit"
            onClick={submit}
            disabled={busy || !token.trim()}
            className="w-full py-1.5 rounded-sm text-[10px] uppercase tracking-[0.15em] bg-amber-500 hover:bg-amber-400 text-black disabled:opacity-50"
          >
            {busy ? "Exchanging…" : "Complete Connection"}
          </button>
          <p className="text-[9px] text-slate-500 leading-relaxed">
            Use this if your Kite app's redirect URL isn't set to our callback. Tokens expire in ~2 minutes.
          </p>
        </div>
      )}
    </div>
  );
}

/**
 * Zerodha TOTP widget.
 *
 * Stores the base32 2FA seed encrypted server-side (Fernet, same key as
 * broker tokens). Displays the current 6-digit code + 30s countdown so
 * daily login = paste password + tap code. Includes an "Open Kite Login"
 * button that opens Zerodha in a new tab.
 *
 * Password is NEVER stored — Zerodha's T&C forbids it and their login page
 * is fully hosted, so we intentionally can't submit it programmatically.
 */
function TOTPWidget({ kiteState }) {
  const [state, setState] = useState({ loading: true, has_seed: false, otp: null, seconds_remaining: 0 });
  const [seedInput, setSeedInput] = useState("");
  const [savingSeed, setSavingSeed] = useState(false);
  const [seedOpen, setSeedOpen] = useState(false);

  const fetchOtp = useCallback(async () => {
    try {
      const { data } = await api.get("/zerodha/totp-current");
      setState({ loading: false, ...data });
    } catch (e) {
      setState({ loading: false, has_seed: false, otp: null, seconds_remaining: 0 });
    }
  }, []);

  useEffect(() => {
    fetchOtp();
    // Refresh once per second so seconds_remaining ticks visibly + we
    // re-fetch fresh 30-second window boundaries from the server.
    const t = setInterval(fetchOtp, 1000);
    return () => clearInterval(t);
  }, [fetchOtp]);

  const saveSeed = async () => {
    const cleaned = seedInput.replace(/\s+/g, "").toUpperCase();
    if (!cleaned) return;
    setSavingSeed(true);
    try {
      await api.put("/zerodha/totp-seed", { seed: cleaned });
      toast.success("TOTP seed saved — current OTP will appear below");
      setSeedInput("");
      setSeedOpen(false);
      fetchOtp();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Invalid TOTP seed");
    } finally {
      setSavingSeed(false);
    }
  };

  const removeSeed = async () => {
    if (!window.confirm("Remove saved TOTP seed? You'll need to re-enter it next time.")) return;
    try {
      await api.delete("/zerodha/totp-seed");
      toast("TOTP seed removed");
      fetchOtp();
    } catch (e) {
      toast.error("Failed to remove seed");
    }
  };

  const copyOtp = async () => {
    if (!state.otp) return;
    try {
      await navigator.clipboard.writeText(state.otp);
      toast.success(`Copied ${state.otp} — paste it in Kite`);
    } catch (e) {
      toast.error("Clipboard denied — tap the code to select");
    }
  };

  const openKiteLogin = async () => {
    // Copy the current OTP to the clipboard first so the user just needs
    // to paste it after entering their password. Then open Kite's hosted
    // login page in a new tab.
    if (state.otp) {
      try {
        await navigator.clipboard.writeText(state.otp);
        toast.success(`OTP ${state.otp} copied — paste it in Kite after your password`);
      } catch (e) {
        // Clipboard permission denied — still open Kite; user can tap the
        // OTP button to copy manually.
        toast(`OTP ${state.otp} — tap the code to copy manually (clipboard denied)`);
      }
    }
    window.open("https://kite.zerodha.com/", "_blank", "noopener,noreferrer");
  };

  if (state.loading) return null;

  // ---- No seed configured ----
  if (!state.has_seed) {
    return (
      <div className="mt-2" data-testid="totp-widget">
        <button
          onClick={() => setSeedOpen(!seedOpen)}
          className="w-full text-[10px] uppercase tracking-[0.15em] text-slate-400 hover:text-blue-400 py-1"
          data-testid="totp-configure-toggle"
        >
          <Shield size={10} className="inline mr-1" /> Set Zerodha 2FA seed (optional)
        </button>
        {seedOpen && (
          <div className="mt-2 space-y-2">
            <input
              data-testid="totp-seed-input"
              value={seedInput}
              onChange={(e) => setSeedInput(e.target.value)}
              placeholder="base32 seed from Zerodha 2FA QR"
              className="w-full bg-slate-900 border border-slate-700 rounded-sm px-2 py-1.5 text-xs font-mono focus:border-blue-500 outline-none"
              spellCheck={false}
            />
            <button
              data-testid="totp-seed-save"
              onClick={saveSeed}
              disabled={savingSeed || !seedInput.trim()}
              className="w-full py-1.5 rounded-sm text-[10px] uppercase tracking-[0.15em] bg-blue-600 hover:bg-blue-500 text-white disabled:opacity-50"
            >
              {savingSeed ? "Saving…" : "Save Seed (encrypted)"}
            </button>
            <p className="text-[9px] text-slate-500 leading-relaxed">
              Stored encrypted (Fernet AES-128). The seed alone can't log in — you still enter your password each time. Password is intentionally never stored (Zerodha T&C). By saving, you acknowledge our <a href="/privacy" className="text-blue-400 hover:underline">Privacy Policy</a>.
            </p>
          </div>
        )}
      </div>
    );
  }

  // ---- Seed configured — show current OTP ----
  const showLoginBtn = kiteState !== "CONNECTED";
  return (
    <div className="mt-3 border-t border-slate-800 pt-3" data-testid="totp-widget">
      <div className="text-[10px] uppercase tracking-[0.15em] text-slate-500 mb-1 flex items-center justify-between">
        <span><Shield size={10} className="inline mr-1" /> Zerodha 2FA</span>
        <button
          onClick={removeSeed}
          className="text-slate-600 hover:text-red-400"
          title="Remove saved TOTP seed"
          data-testid="totp-seed-remove"
        >
          <Trash2 size={11} />
        </button>
      </div>
      <button
        data-testid="totp-code"
        onClick={copyOtp}
        className="w-full flex items-center justify-between px-3 py-2 bg-slate-900 hover:bg-slate-800 border border-slate-700 rounded-sm transition-colors"
        title="Click to copy"
      >
        <span className="text-2xl font-mono tracking-[0.25em] text-blue-300">
          {state.otp}
        </span>
        <span className="flex items-center gap-2 text-[10px] text-slate-500 uppercase tracking-[0.1em]">
          {state.seconds_remaining}s
          <Copy size={12} />
        </span>
      </button>
      {/* Countdown bar — red under 5s */}
      <div className="mt-1 h-1 w-full bg-slate-900 rounded-sm overflow-hidden">
        <div
          className={`h-full transition-all duration-1000 ${state.seconds_remaining <= 5 ? "bg-red-500" : "bg-blue-500"}`}
          style={{ width: `${(state.seconds_remaining / 30) * 100}%` }}
        />
      </div>
      {showLoginBtn && (
        <button
          data-testid="totp-open-kite-btn"
          onClick={openKiteLogin}
          className="mt-2 w-full py-1.5 rounded-sm text-[10px] uppercase tracking-[0.15em] border border-slate-700 hover:border-blue-500 hover:bg-blue-500/10 text-slate-300 hover:text-blue-300 flex items-center justify-center gap-1"
          title="Copies the current OTP to your clipboard and opens Kite in a new tab"
        >
          <ExternalLink size={11} /> Copy OTP &amp; Open Kite
        </button>
      )}
    </div>
  );
}

function Card({ title, right, children, testid, accent }) {
  return (
    <div className={`surface rounded-sm p-4 border-l-2 ${cardAccentClass(accent)}`} data-testid={testid}>
      <div className="flex items-center justify-between mb-3">
        <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">{title}</div>
        {right}
      </div>
      {children}
    </div>
  );
}

function FuturesTile({ tile }) {
  if (!tile) return null;
  const trendIcon = trendIconFor(tile.trend);
  const displayPrice = tile.live_ltp ?? tile.last_close;
  const isLive = tile.source === "LIVE";
  return (
    <div className="surface rounded-sm p-4" data-testid={`futures-tile-${tile.underlying}`}>
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="flex items-center gap-2">
            <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">{tile.underlying} Futures</div>
            <span className={`text-[9px] uppercase tracking-[0.15em] px-1.5 py-0.5 rounded-sm ${sourceBadgeClass(tile.source, isLive)}`}>{tile.source || "—"}</span>
          </div>
          <div className="text-2xl font-mono font-medium mt-1">{fmtNum(displayPrice)}</div>
          {tile.live_ltp && tile.last_close && tile.live_ltp !== tile.last_close && (
            <div className="text-[10px] text-slate-500 font-mono">last close: {fmtNum(tile.last_close)}</div>
          )}
        </div>
        <div className="text-right">
          <div className="flex items-center gap-1 text-xs uppercase tracking-[0.15em] text-slate-400">
            {trendIcon} {tile.trend}
          </div>
          <div className={`mt-1 text-[10px] uppercase tracking-[0.15em] ${breakoutTextClass(tile.breakout_state)}`}>
            Breakout: {tile.breakout_state}
          </div>
        </div>
      </div>
      <div className="grid grid-cols-3 gap-2 text-[11px] tabnums">
        <div><div className="text-slate-500 text-[9px] uppercase">ADX14</div><div className="font-mono">{fmtNum(tile.adx14, 1)}</div></div>
        <div><div className="text-slate-500 text-[9px] uppercase">ATR14</div><div className="font-mono">{fmtNum(tile.atr14, 1)}</div></div>
        <div><div className="text-slate-500 text-[9px] uppercase">Vol Ratio</div><div className="font-mono">{fmtNum(tile.volume_ratio, 2)}</div></div>
        <div><div className="text-slate-500 text-[9px] uppercase">EMA20/50</div><div className="font-mono">{fmtNum(tile.ema20, 0)} / {fmtNum(tile.ema50, 0)}</div></div>
        <div><div className="text-slate-500 text-[9px] uppercase">VWAP</div><div className="font-mono">{fmtNum(tile.vwap, 0)}</div></div>
        <div><div className="text-slate-500 text-[9px] uppercase">OR High/Low</div><div className="font-mono">{fmtNum(tile.opening_high, 0)}/{fmtNum(tile.opening_low, 0)}</div></div>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const [tiles, setTiles] = useState({});
  const [risk, setRisk] = useState(null);
  const [kite, setKite] = useState(null);
  const [positions, setPositions] = useState([]);
  const [perf, setPerf] = useState(null);
  const [signals, setSignals] = useState([]);
  const [planningDiag, setPlanningDiag] = useState(null);
  const [events, setEvents] = useState([]);
  const [running, setRunning] = useState(false);
  const [zerodhaCallbackCode, setZerodhaCallbackCode] = useState(null);
  const [v2Shadow, setV2Shadow] = useState(null);
  const [screener, setScreener] = useState(null);
  const [bridge, setBridge] = useState(null);

  const refresh = async () => {
    try {
      const [t, r, k, p, pr, s, e, shadow, scr, br] = await Promise.all([
        api.get("/dashboard/tiles"),
        api.get("/risk-state"),
        api.get("/zerodha/status"),
        api.get("/paper-positions"),
        api.get("/performance"),
        api.get("/signals?limit=10"),
        api.get("/system-events?limit=10"),
        api.get("/strategy/v2-shadow").catch(() => ({ data: null })),
        api.get("/screener/shortlist").catch(() => ({ data: null })),
        api.get("/screener/bias").catch(() => ({ data: null })),
      ]);
      setTiles(t.data);
      setRisk(r.data);
      setKite(k.data);
      setPositions(p.data);
      setPerf(pr.data);
      setSignals(s.data);
      setEvents(e.data);
      setV2Shadow(shadow.data);
      setScreener(scr.data);
      setBridge(br.data);
    } catch (err) {
      if (process.env.NODE_ENV === "development") {
        console.error(err);
      }
    }
  };

  useEffect(() => {
    refresh();
    const t = setInterval(refresh, 15000);
    return () => clearInterval(t);
    // `refresh` is a stable async closure over state setters (all stable
    // refs from React). Adding it to deps would re-schedule the interval
    // on every render — the current mount-once behavior is intentional.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Surface Kite OAuth callback outcome (server redirects to /dashboard?zerodha=<code>).
  // Without this the exchange failure was silent and users just saw the old EXPIRED
  // state, mistaking a failed OAuth for a stuck UI.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get("zerodha");
    if (!code) return;
    setZerodhaCallbackCode(code);
    // Defer sonner toast to next tick — <Toaster> from src/components/ui/sonner
    // is mounted inside the same Layout tree and may not have subscribed by the
    // time this effect runs on the very first render after the OAuth redirect.
    // A 0ms setTimeout lets React flush the initial mount, guaranteeing the
    // toast is emitted after the Toaster is listening.
    setTimeout(() => {
      if (code === "connected") {
        toast.success("Zerodha connected", {
          description: "Live feed will auto-start during market hours.",
        });
        refresh();
      } else if (code === "login_required") {
        toast.error("Zerodha callback failed: app session missing", {
          description:
            "Your login cookie was not sent back with the Kite redirect. Try 'Paste request_token instead' below.",
          duration: 12000,
        });
      } else if (code === "exchange_failed") {
        toast.error("Zerodha token exchange failed", {
          description:
            "Kite rejected the request_token. Check your KITE_API_SECRET, or use 'Paste request_token instead' below.",
          duration: 12000,
        });
      } else if (code === "error") {
        toast.error("Zerodha login not completed", {
          description:
            "The Kite login flow did not report success. Try again or use 'Paste request_token instead' below.",
          duration: 12000,
        });
      }
    }, 0);
    // Scrub the query string so a page refresh doesn't re-fire the toast.
    const clean = window.location.pathname + window.location.hash;
    window.history.replaceState({}, "", clean);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-dismiss any stale Kite callback banner the moment the broker status
  // resolves to CONNECTED. Guards against a suspended mobile tab restoring an
  // old `?zerodha=csrf_state_invalid` (or similar) URL and re-flashing the
  // banner even though the connection is actually live in the DB.
  useEffect(() => {
    if (kite?.state === "CONNECTED" && zerodhaCallbackCode
        && zerodhaCallbackCode !== "connected") {
      setZerodhaCallbackCode(null);
    }
  }, [kite?.state, zerodhaCallbackCode]);

  const connectZerodha = async () => {
    try {
      const { data } = await api.get("/zerodha/connect");
      toast(
        "Redirecting to Zerodha. If Kite says 'Invalid api_key' or redirects to 127.0.0.1, use 'Paste request_token instead' below.",
        { duration: 8000 },
      );
      setTimeout(() => (window.location.href = data.url), 800);
    } catch (e) {
      toast.error("Failed to start Zerodha connect");
    }
  };

  const runDemo = async () => {
    setRunning(true);
    try {
      const { data } = await api.post("/demo/run-session");
      toast.success(`Demo session complete — ${data.signals_created} signals, ${data.trades_created} trades`);
      refresh();
    } catch (e) {
      toast.error("Demo session failed");
    } finally {
      setRunning(false);
    }
  };

  const [pwaCode, setPwaCode] = useState(null);
  const [pwaMinting, setPwaMinting] = useState(false);

  const mintPwaTransfer = async () => {
    setPwaMinting(true);
    try {
      const { data } = await api.post("/auth/pwa-transfer/mint");
      setPwaCode(data);
      // Best-effort clipboard copy
      try {
        await navigator.clipboard.writeText(data.code);
        toast.success(`PWA code ${data.code} copied — paste in the PWA within 5 min`);
      } catch {
        toast(`PWA code: ${data.code} (tap to copy manually)`);
      }
    } catch (e) {
      toast.error("Failed to mint transfer code");
    } finally {
      setPwaMinting(false);
    }
  };

  const openPos = positions[0];
  const netPnl = perf?.net_pnl ?? 0;
  const gross = perf?.gross_pnl ?? 0;
  const totalCharges = perf?.total_charges ?? 0;
  const dailyUsed = risk?.daily_loss_limit_used_fraction ?? 0;
  const remaining = ((risk?.max_trades_per_day ?? 2) - (risk?.trades_today ?? 0));

  return (
    <div className="space-y-4" data-testid="dashboard-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-medium tracking-tight">Dashboard</h1>
          <p className="text-xs text-slate-500 uppercase tracking-[0.15em] mt-1">
            Session overview · {risk?.trading_date || "—"}
          </p>
        </div>
        <div className="flex gap-2">
          <button
            data-testid="pwa-transfer-mint-btn"
            onClick={mintPwaTransfer}
            disabled={pwaMinting}
            title="Mint a 6-char code to activate your session on the installed PWA (iOS)"
            className="px-3 py-2 rounded-sm text-xs uppercase tracking-[0.15em] border border-amber-500/40 text-amber-300 hover:bg-amber-500/10 transition-colors flex items-center gap-2 disabled:opacity-50"
          >
            <KeyRound size={14} /> {pwaMinting ? "…" : "Transfer to PWA"}
          </button>
          <button
            data-testid="run-demo-btn"
            onClick={runDemo}
            disabled={running}
            className="px-3 py-2 rounded-sm text-xs uppercase tracking-[0.15em] border border-blue-500 text-blue-400 hover:bg-blue-500 hover:text-white transition-colors flex items-center gap-2 disabled:opacity-50"
          >
            <PlayCircle size={14} /> {running ? "Running…" : "Run Demo Session"}
          </button>
          <button
            onClick={refresh}
            className="px-3 py-2 rounded-sm text-xs uppercase tracking-[0.15em] border border-slate-700 hover:bg-slate-800/50"
          >
            <RotateCw size={14} className="inline mr-2" />
            Refresh
          </button>
        </div>
      </div>

      {/* PWA transfer code modal */}
      <PwaTransferModal codeData={pwaCode} onClose={() => setPwaCode(null)} />

      {/* V2 Shadow P&L card (Phase 6) */}
      <V2ShadowCard v2Shadow={v2Shadow} />

      {/* Zerodha callback outcome banner (persistent, dismissable) */}
      <ZerodhaCallbackBanner code={zerodhaCallbackCode}
                              onDismiss={() => setZerodhaCallbackCode(null)} />

      {/* Zerodha + Capital cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card title="Zerodha Connection" testid="card-zerodha" accent={kite?.state === "CONNECTED" ? "pos" : "warn"}>
          <div className="text-lg font-mono">{kite?.state || "…"}</div>
          <div className="mt-1 text-xs text-slate-400">
            {kite?.state === "CONNECTED"
              ? kite?.broker_user_name || "Connected"
              : kite?.demo_mode
              ? "Demo mode enabled"
              : "Not connected"}
          </div>
          {kite && kite.state !== "CONNECTED" && (
            <>
              <button
                data-testid="connect-zerodha-btn"
                onClick={connectZerodha}
                className="mt-3 w-full py-2 rounded-sm text-xs uppercase tracking-[0.15em] bg-blue-600 hover:bg-blue-500 text-white flex items-center justify-center gap-2"
              >
                <Link2 size={14} /> {kite?.state === "EXPIRED" ? "Reconnect" : "Connect Zerodha"}
              </button>
              <ManualExchange onDone={refresh} />
            </>
          )}
          <TOTPWidget kiteState={kite?.state} />
          <div className="mt-2 text-[10px] uppercase tracking-[0.15em] text-slate-500">
            Key: <span className="font-mono">{kite?.key_fingerprint || "—"}</span>
          </div>
        </Card>

        <Card title="Allocated / Available" testid="card-capital">
          <div className="text-lg font-mono">{fmtMoney(risk?.allocated_capital)}</div>
          <div className="text-xs text-slate-400 mt-1">Available (paper): {fmtMoney((risk?.allocated_capital ?? 0) + netPnl)}</div>
          <div className="text-xs text-slate-500 mt-2">Deployed premium: {openPos ? fmtMoney(openPos.entry_price * openPos.quantity) : fmtMoney(0)}</div>
        </Card>

        <Card title="Net P&L (today)" testid="card-pnl" accent={netPnl >= 0 ? "pos" : "neg"}>
          <div className={`text-2xl font-mono ${netPnl >= 0 ? "text-pos" : "text-neg"}`}>
            {netPnl >= 0 ? "+" : ""}{fmtMoney(netPnl)}
          </div>
          <div className="text-[11px] text-slate-500 mt-1">
            Gross: <span className="font-mono text-slate-300">{fmtMoney(gross)}</span>
          </div>
          <div className="text-[11px] text-slate-500">
            Charges: <span className="font-mono text-slate-300">{fmtMoney(totalCharges)}</span>
          </div>
        </Card>

        <Card title="Risk Budget" testid="card-risk" accent={dailyUsed > 0.5 ? "warn" : "pos"}>
          <div className="text-xs text-slate-400 mb-1">Daily loss used</div>
          <div className="w-full h-2 bg-slate-800 rounded-sm overflow-hidden">
            <div
              className={`h-full ${dailyUsed > 0.5 ? "bg-amber-500" : "bg-emerald-500"}`}
              style={{ width: `${Math.min(100, dailyUsed * 100)}%` }}
            />
          </div>
          <div className="mt-2 flex justify-between text-[11px] font-mono">
            <span>{fmtPct(dailyUsed, 0)} used</span>
            <span className="text-slate-500">of {fmtPct(risk?.daily_loss_limit_fraction ?? 0.02, 0)}</span>
          </div>
          <div className="mt-3 text-[11px] text-slate-500">
            Trades left today: <span className="text-slate-200 font-mono">{remaining}/{risk?.max_trades_per_day ?? 2}</span>
          </div>
          <div className="text-[11px] text-slate-500">
            Consecutive losses: <span className="text-slate-200 font-mono">{risk?.consecutive_losses ?? 0}</span>
          </div>
        </Card>
      </div>

      {/* Futures tiles — driven by the strategy universe so SENSEX (and any
          future addition) auto-appears. */}
      <div className={`grid grid-cols-1 gap-4 ${
        Object.keys(tiles).length >= 3 ? "lg:grid-cols-3 md:grid-cols-2" :
        "md:grid-cols-2"
      }`}>
        {Object.entries(tiles).map(([u, tile]) => (
          <FuturesTile key={u} tile={tile} />
        ))}
        {Object.keys(tiles).length === 0 && (
          <div className="col-span-full text-center text-slate-500 py-8 text-xs">
            No underlyings enabled in the strategy universe.
          </div>
        )}
      </div>

      {/* Head-to-head: today's realized P&L, ORB vs MTF Impulse. Polls the
          dedicated /mtf/vs-orb-today endpoint every 15s. */}
      <MtfVsOrbTile />

      {/* NIFTY 50 pre-market screener top-5 */}
      {(screener?.top_10?.length || 0) > 0 && (
        <div className="surface rounded-sm p-4" data-testid="dashboard-screener-tile">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">
                Pre-Market Screener
              </div>
              <div className="text-sm text-slate-200 mt-1">
                Top NIFTY 50 candidates
                {screener.is_stale && (
                  <span className="ml-2 text-[10px] text-amber-400 uppercase">previous session</span>
                )}
              </div>
            </div>
            <a
              href="/screener"
              className="text-[11px] uppercase tracking-[0.15em] text-blue-400 hover:text-blue-300"
              data-testid="dashboard-screener-link"
            >
              Open →
            </a>
          </div>
          {/* Screener → Signal Bridge banner */}
          {bridge && bridge.enabled && (
            <div
              className={`mb-3 rounded-sm px-3 py-2 text-[11px] border ${
                bridge.long_boost_applies || bridge.short_boost_applies
                  ? "border-blue-500/50 bg-blue-500/10 text-blue-200"
                  : "border-slate-700 bg-slate-800/40 text-slate-400"
              }`}
              data-testid="dashboard-bridge-tile"
            >
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div>
                  <span className="uppercase tracking-[0.15em] text-[10px] mr-2">
                    Bridge:
                  </span>
                  Bias{" "}
                  <span className={`font-mono ${bridge.bias > 0 ? "text-pos" : bridge.bias < 0 ? "text-neg" : ""}`}>
                    {bridge.bias > 0 ? "+" : ""}{bridge.bias?.toFixed(2)}%
                  </span>
                  {" · favors "}
                  <span className="font-mono">{bridge.favored}</span>
                </div>
                <div className="text-[10px] text-slate-400 italic">
                  {bridge.reason}
                </div>
                {(bridge.long_boost_applies || bridge.short_boost_applies) && (
                  <div className="font-mono text-blue-300">
                    Next matching signal → {bridge.boost_factor_configured}× lots
                  </div>
                )}
              </div>
            </div>
          )}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
            {screener.top_10.slice(0, 5).map((r, i) => (
              <div key={r.symbol} className="border border-slate-800 rounded-sm px-3 py-2">
                <div className="flex items-baseline justify-between">
                  <span className="text-[10px] text-slate-500">#{i + 1}</span>
                  <span className="text-[10px] font-mono text-blue-300">{r.composite_score?.toFixed(2)}</span>
                </div>
                <div className="text-sm font-mono text-slate-100 mt-0.5">{r.symbol}</div>
                <div className="text-[10px] text-slate-500 mt-0.5 flex justify-between">
                  <span>{r.sector}</span>
                  <span className={r.gap_pct > 0 ? "text-pos" : r.gap_pct < 0 ? "text-neg" : ""}>
                    {r.gap_pct >= 0 ? "+" : ""}{r.gap_pct?.toFixed(1)}%
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Open position */}
      <Card title="Current Paper Position" testid="card-open-position">
        {!openPos && <div className="text-sm text-slate-500 py-6 text-center">No open position.</div>}
        {openPos && (
          <div className="grid grid-cols-2 md:grid-cols-6 gap-4 text-sm">
            <div><div className="text-[10px] uppercase text-slate-500">Symbol</div><div className="font-mono">{openPos.tradingsymbol}</div></div>
            <div><div className="text-[10px] uppercase text-slate-500">Entry</div><div className="font-mono">{fmtNum(openPos.entry_price)}</div></div>
            <div><div className="text-[10px] uppercase text-slate-500">Stop Trigger</div><div className="font-mono text-neg">{fmtNum(openPos.stop_trigger)}</div></div>
            <div><div className="text-[10px] uppercase text-slate-500">Target</div><div className="font-mono text-pos">{fmtNum(openPos.target_price)}</div></div>
            <div><div className="text-[10px] uppercase text-slate-500">Net Risk</div><div className="font-mono">{fmtMoney(openPos.net_risk)}</div></div>
            <div><div className="text-[10px] uppercase text-slate-500">Lots</div><div className="font-mono">{openPos.lots} × {openPos.quantity / openPos.lots}</div></div>
          </div>
        )}
      </Card>

      {/* Recent signals + events */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card title="Recent Signals" testid="card-signals">
          {signals.length === 0 && <div className="text-sm text-slate-500 py-6 text-center">No signals yet.</div>}
          <div className="space-y-2">
            {signals.slice(0, 6).map((s) => (
              <div key={s.id} className="text-xs py-1.5 border-b border-slate-800 last:border-0">
                <div className="flex justify-between">
                  <div>
                    <span className="font-mono text-slate-300">{s.candle_timestamp}</span>
                    <span className="ml-2 text-slate-500">{s.underlying}</span>
                    <span className={`ml-2 ${s.direction === "LONG" ? "text-pos" : "text-neg"}`}>{s.option_type}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className={`text-[10px] uppercase tracking-[0.15em] ${s.accepted ? "text-pos" : "text-slate-500"}`}>
                      {s.accepted ? "Accepted" : s.rejection_code || "Rejected"}
                    </div>
                    {s.plan_breakdown && (
                      <button
                        data-testid={`planning-diagnostics-btn-${s.id}`}
                        onClick={() => setPlanningDiag(s)}
                        className="text-[10px] uppercase tracking-[0.15em] text-blue-400 hover:text-blue-300"
                        title="Show planning diagnostics"
                      >
                        Diag →
                      </button>
                    )}
                  </div>
                </div>
                {/* Compact one-line summary of the planning breakdown */}
                {s.plan_breakdown && s.plan_breakdown.verdict && (
                  <div className="mt-1 text-[10px] text-slate-500 font-mono">
                    v{s.config_version || "?"}·{(s.config_hash || "").slice(0, 6)}
                    {" · guard="}{s.plan_breakdown.verdict.guard || "ok"}
                    {" · Δ="}{(((s.option || {}).estimated_delta) || 0).toFixed(3)}
                    {" · charges=₹"}{Number(s.estimated_charges || 0).toFixed(0)}
                  </div>
                )}
              </div>
            ))}
          </div>
        </Card>

        <Card title="System Events" testid="card-events">
          {events.length === 0 && <div className="text-sm text-slate-500 py-6 text-center">No events.</div>}
          <div className="space-y-1 text-[11px] font-mono">
            {events.slice(0, 8).map((e) => (
              <div key={`${e.timestamp}-${e.component}-${e.message}`} className="flex gap-3">
                <span className="text-slate-600">{new Date(e.timestamp).toLocaleTimeString("en-IN", { timeZone: "Asia/Kolkata" })}</span>
                <span className="text-slate-500">[{e.component}]</span>
                <span className="text-slate-300">{e.message}</span>
              </div>
            ))}
          </div>
        </Card>
      </div>

      {/* Planning Diagnostics drawer (spec §UI REQUIREMENTS) */}
      <PlanningDiagnosticsDrawer signal={planningDiag}
                                   onClose={() => setPlanningDiag(null)} />
    </div>
  );
}


// ===========================================================================
// Dashboard sub-components — extracted from the render tree so the main
// component reads as an orchestrator. Each sub-component is < 100 lines
// and receives only the data it needs.
// ===========================================================================

function pnlTextClass(v) { return Number(v || 0) >= 0 ? "text-pos" : "text-neg"; }
function pnlInverseClass(v) { return Number(v || 0) <= 0 ? "text-pos" : "text-neg"; }
function deltaBorderClass(v) {
  return Number(v || 0) >= 0 ? "border-l-emerald-500" : "border-l-red-500";
}
function deltaCommentary(v) {
  return Number(v || 0) >= 0 ? "V2 would have done better" : "V1 outperformed V2";
}
function callbackToneClass() { return "border-l-2 border-l-amber-500"; }

// ---------------------------------------------------------------------------
// V2 Shadow P&L card
// ---------------------------------------------------------------------------
function V2ShadowCard({ v2Shadow }) {
  if (!v2Shadow?.totals) return null;
  const totals = v2Shadow.totals;
  const rejects = v2Shadow.rejection_breakdown || {};
  return (
    <div className="surface rounded-sm p-4" data-testid="v2-shadow-card">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-blue-300">
            V2 Shadow P&amp;L — counterfactual
          </div>
          <div className="text-[10px] text-slate-500 mt-0.5">
            If V2 rules were running today, this is what would have happened
          </div>
        </div>
        <a href="/comparison"
           className="text-[10px] uppercase tracking-[0.15em] text-slate-400 hover:text-blue-300">
          Full comparison →
        </a>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs font-mono">
        <V2ShadowMetric borderClass="border-l-blue-500" label="V2 Would Have Made"
                         value={totals.v2_taken_pnl} valueTestId="v2-taken-pnl"
                         countLabel={`${totals.v2_taken_count} trades taken`}
                         countTestId="v2-taken-count"
                         valueClass={pnlTextClass(totals.v2_taken_pnl)} />
        <V2ShadowMetric borderClass="border-l-amber-500" label="V2 Would Have Skipped"
                         value={totals.v2_avoided_pnl} valueTestId="v2-avoided-pnl"
                         countLabel={`${totals.v2_avoided_count} trades avoided`}
                         countTestId="v2-avoided-count"
                         valueClass={pnlInverseClass(totals.v2_avoided_pnl)} />
        <V2ShadowMetric borderClass="border-l-slate-500" label="V1 Actual"
                         value={totals.v1_actual_pnl} valueTestId="v1-actual-pnl"
                         countLabel={`${totals.total_closed_trades} closed trades`}
                         valueClass={pnlTextClass(totals.v1_actual_pnl)} />
        <V2ShadowMetric borderClass={deltaBorderClass(totals.delta_vs_actual)}
                         label="V2 minus V1"
                         value={totals.delta_vs_actual}
                         valueTestId="v2-delta-vs-actual"
                         valueClass={pnlTextClass(totals.delta_vs_actual)}
                         signed
                         countLabel={deltaCommentary(totals.delta_vs_actual)} />
      </div>
      {Object.keys(rejects).length > 0 ? (
        <div className="mt-4 border-t border-slate-800 pt-3"
             data-testid="v2-rejection-breakdown">
          <div className="text-[10px] uppercase tracking-[0.15em] text-slate-500 mb-2">
            V2 rejection breakdown
          </div>
          <div className="flex flex-wrap gap-2">
            {Object.entries(rejects).map(([code, info]) => (
              <div key={code}
                   className="px-2 py-1 border border-slate-700 rounded-sm text-[10px] font-mono"
                   data-testid={`v2-reject-${code}`}>
                <span className="text-slate-400">{code}</span>
                <span className="text-slate-500 ml-2">
                  {info.count} × {info.pnl_skipped >= 0 ? "+" : ""}₹
                  {Number(info.pnl_skipped).toLocaleString("en-IN")}
                </span>
              </div>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}

function V2ShadowMetric({ borderClass, label, value, valueTestId, valueClass,
                          countLabel, countTestId, signed }) {
  const sign = signed && Number(value || 0) >= 0 ? "+" : "";
  return (
    <div className={`border-l-2 pl-3 ${borderClass}`}>
      <div className="text-[10px] uppercase tracking-[0.15em] text-slate-500 mb-1">
        {label}
      </div>
      <div className={`text-lg ${valueClass}`} data-testid={valueTestId}>
        {sign}₹{Number(value || 0).toLocaleString("en-IN")}
      </div>
      <div className="text-[10px] text-slate-500 mt-1" data-testid={countTestId}>
        {countLabel}
      </div>
    </div>
  );
}


// ---------------------------------------------------------------------------
// Zerodha callback banner — persistent, dismissable amber tile.
// ---------------------------------------------------------------------------
function ZerodhaCallbackBanner({ code, onDismiss }) {
  if (!code || code === "connected") return null;
  return (
    <div data-testid="zerodha-callback-banner"
         className={`surface rounded-sm p-4 ${callbackToneClass()} flex items-start justify-between gap-4`}>
      <div className="text-xs text-slate-200 leading-relaxed">
        <div className="text-[10px] uppercase tracking-[0.2em] text-amber-400 mb-1">
          Zerodha login didn&apos;t complete — <span className="font-mono">{code}</span>
        </div>
        <ZerodhaCallbackReason code={code} />
      </div>
      <button data-testid="zerodha-callback-banner-close"
              onClick={onDismiss}
              className="text-slate-500 hover:text-slate-200 text-xs uppercase tracking-[0.15em]">
        Dismiss
      </button>
    </div>
  );
}

function ZerodhaCallbackReason({ code }) {
  if (code === "login_required") {
    return (
      <>
        Your MyEdge session cookie was not sent back with the Kite redirect.
        This usually means the redirect URL registered in the Zerodha Dev
        Console doesn&apos;t match
        <span className="font-mono"> https://myedgetrader.com/api/zerodha/callback</span>.
        Use <span className="text-blue-400 font-medium">Paste request_token instead</span> below to finish the exchange.
      </>
    );
  }
  if (code === "exchange_failed") {
    return (
      <>
        Kite rejected the <span className="font-mono">request_token</span>. Verify KITE_API_SECRET,
        or paste a fresh <span className="font-mono">request_token</span> below within 2 minutes.
      </>
    );
  }
  if (code === "error") {
    return (
      <>
        Kite did not report a successful login. Try Reconnect again, or paste
        the <span className="font-mono">request_token</span> below.
      </>
    );
  }
  return null;
}


// ---------------------------------------------------------------------------
// PWA transfer code modal — shown after minting a 6-char transfer code.
// ---------------------------------------------------------------------------
function PwaTransferModal({ codeData, onClose }) {
  if (!codeData) return null;
  const minutes = Math.round(codeData.expires_in_seconds / 60);
  return (
    <div className="fixed inset-0 z-40 bg-black/70 backdrop-blur-md flex items-center justify-center p-4"
         onClick={onClose}
         data-testid="pwa-transfer-modal">
      <div className="surface rounded-sm max-w-sm w-full p-6"
           onClick={(e) => e.stopPropagation()}>
        <div className="text-[10px] uppercase tracking-[0.2em] text-amber-300 mb-2">
          PWA Transfer Code
        </div>
        <div className="text-4xl font-mono tracking-[0.5em] text-center py-6 text-slate-100 cursor-pointer select-all"
             onClick={() => navigator.clipboard?.writeText(codeData.code)}
             data-testid="pwa-transfer-code-value">
          {codeData.code}
        </div>
        <p className="text-xs text-slate-400 leading-relaxed">
          Open the MyEdge PWA on your home screen, tap the &quot;Paste PWA transfer code&quot; field
          on the login page, and enter this code. Expires in {minutes} minutes. Single use.
        </p>
        <div className="flex justify-end mt-4">
          <button onClick={onClose}
                  className="px-4 py-2 rounded-sm border border-slate-700 text-slate-300 hover:bg-slate-800/50 text-xs uppercase tracking-[0.15em]">
            Close
          </button>
        </div>
      </div>
    </div>
  );
}


// ---------------------------------------------------------------------------
// Planning Diagnostics drawer — right-side sliding panel with breakdown.
// ---------------------------------------------------------------------------
const DIAGNOSTIC_SECTIONS = [
  "instrument", "market", "config", "stop", "risk", "target", "verdict",
];
const DIAGNOSTIC_LABELS = {
  instrument: "Instrument", market: "Market", config: "Config",
  stop: "Stop Calculation", risk: "Risk Calculation",
  target: "Target Calculation", verdict: "Verdict / Guard",
};

function PlanningDiagnosticsDrawer({ signal, onClose }) {
  if (!signal) return null;
  const bd = signal.plan_breakdown || {};
  const directionClass = signal.direction === "LONG" ? "text-pos" : "text-neg";
  const verdictText = signal.accepted
    ? "ACCEPTED"
    : `REJECTED: ${signal.rejection_reason || signal.rejection_code}`;
  return (
    <div data-testid="planning-diagnostics-drawer"
         className="fixed inset-0 z-50 flex items-end justify-end bg-black/60"
         onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()}
           className="h-full w-full max-w-2xl bg-slate-900 border-l border-slate-800 overflow-y-auto p-6 shadow-2xl">
        <div className="flex items-center justify-between mb-4">
          <div className="text-[10px] uppercase tracking-[0.2em] text-amber-400">
            Planning Diagnostics
          </div>
          <button data-testid="planning-diagnostics-close"
                  onClick={onClose}
                  className="text-slate-500 hover:text-slate-200 text-xs uppercase tracking-[0.15em]">
            Close
          </button>
        </div>
        <div className="text-sm text-slate-200 mb-4">
          <div className="font-mono text-lg">
            {signal.underlying}
            <span className="mx-2 text-slate-600">·</span>
            <span className={directionClass}>{signal.option_type}</span>
            <span className="mx-2 text-slate-600">·</span>
            <span className="text-slate-400">{signal.candle_timestamp}</span>
          </div>
          <div className={`text-xs mt-1 ${signal.accepted ? "text-pos" : "text-neg"}`}>
            {verdictText}
          </div>
          <div className="text-[10px] text-slate-500 mt-1 font-mono">
            config_version={signal.config_version} · config_hash={signal.config_hash}
          </div>
        </div>
        {DIAGNOSTIC_SECTIONS.map((section) => (
          <DiagnosticBlock key={section}
                            label={DIAGNOSTIC_LABELS[section]}
                            payload={bd[section]} />
        ))}
        <DiagnosticBlock label="Cost — estimated_charges"
                          payload={{ value_rupees: signal.estimated_charges }} />
      </div>
    </div>
  );
}

function DiagnosticBlock({ label, payload }) {
  return (
    <div className="mb-4">
      <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-1.5">
        {label}
      </div>
      <pre className="text-[11px] font-mono bg-slate-950 border border-slate-800 rounded-sm p-3 overflow-x-auto text-slate-300">
        {JSON.stringify(payload || {}, null, 2)}
      </pre>
    </div>
  );
}



// ---------------------------------------------------------------------------
// MTF vs ORB head-to-head tile — today's realized P&L for each algo, side by
// side, with a delta pill so you can see which one is winning right now.
// ---------------------------------------------------------------------------
function MtfVsOrbTile() {
  const [data, setData] = React.useState(null);
  React.useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const r = await api.get("/mtf/vs-orb-today");
        if (alive) setData(r.data);
      } catch (err) { console.error("vs-orb tile", err); }
    };
    load();
    const t = setInterval(load, 15000);
    return () => { alive = false; clearInterval(t); };
  }, []);
  if (!data) {
    return (
      <div className="surface rounded-sm p-4" data-testid="dashboard-vs-orb-tile">
        <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">
          ORB vs MTF · today
        </div>
        <p className="text-xs text-slate-500 mt-2">Loading…</p>
      </div>
    );
  }
  const orb = data.orb || {};
  const mtf = data.mtf || {};
  const delta = Number(data.delta_mtf_minus_orb || 0);
  const winner = delta > 0 ? "MTF" : delta < 0 ? "ORB" : "TIE";
  const winnerClass = delta > 0 ? "text-pos" : delta < 0 ? "text-slate-300" : "text-slate-400";
  return (
    <div className="surface rounded-sm p-4" data-testid="dashboard-vs-orb-tile">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">
            ORB vs MTF · today
          </div>
          <div className="text-[10px] text-slate-500 mt-0.5">
            Realized P&amp;L side-by-side — updates every 15s
          </div>
        </div>
        <div className={`text-[11px] font-mono uppercase tracking-[0.15em] ${winnerClass}`}>
          {winner === "TIE" ? "even" : `${winner} leading`}
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <VsOrbCard label="ORB (options)" data={orb}
                    testid="dashboard-vs-orb-orb" borderClass="border-l-slate-500" />
        <VsOrbCard label="MTF Impulse" data={mtf}
                    testid="dashboard-vs-orb-mtf" borderClass="border-l-purple-500" />
        <div className={`border-l-2 pl-3 ${delta >= 0 ? "border-l-emerald-500" : "border-l-red-500"}`}
             data-testid="dashboard-vs-orb-delta">
          <div className="text-[10px] uppercase tracking-[0.15em] text-slate-500 mb-1">
            MTF minus ORB
          </div>
          <div className={`text-2xl font-mono ${delta >= 0 ? "text-pos" : "text-neg"}`}>
            {delta >= 0 ? "+" : ""}₹{delta.toLocaleString("en-IN")}
          </div>
          <div className="text-[10px] text-slate-500 mt-1">
            {winner === "TIE"
              ? "Both algos are level today"
              : `${winner} ahead by ₹${Math.abs(delta).toLocaleString("en-IN")}`}
          </div>
        </div>
      </div>
    </div>
  );
}

function VsOrbCard({ label, data, testid, borderClass }) {
  const pnl = Number(data.realized_pnl || 0);
  const trades = data.trades_closed || 0;
  const wins = data.wins || 0;
  const losses = data.losses || 0;
  const open = data.open_positions || 0;
  const winRate = trades > 0 ? ((wins / trades) * 100).toFixed(0) : "—";
  return (
    <div className={`border-l-2 pl-3 ${borderClass}`} data-testid={testid}>
      <div className="text-[10px] uppercase tracking-[0.15em] text-slate-500 mb-1">
        {label}
      </div>
      <div className={`text-2xl font-mono ${pnl >= 0 ? "text-pos" : "text-neg"}`}
           data-testid={`${testid}-pnl`}>
        ₹{pnl.toLocaleString("en-IN")}
      </div>
      <div className="text-[10px] text-slate-500 mt-1 font-mono">
        {trades} closed · {wins}W / {losses}L · {winRate}% · {open} open
      </div>
    </div>
  );
}

